import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { audiencesService, type AudienceEntriesListResponse } from '../services';
import type {
  AudienceEntryResponse,
  CreateAudienceEntryRequest,
  UpdateAudienceEntryRequest,
  AudienceEntryQuery,
} from '@troia-v3/types';

export const useAudienceEntries = (
  audienceId: string | undefined,
  query?: AudienceEntryQuery
): UseQueryResult<AudienceEntriesListResponse, Error> => {
  return useQuery({
    queryKey: ['audience-entries', audienceId, query],
    queryFn: () => audiencesService.getEntries(audienceId!, query),
    enabled: !!audienceId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateAudienceEntry = (
  audienceId: string | undefined
): UseMutationResult<AudienceEntryResponse, Error, CreateAudienceEntryRequest> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAudienceEntryRequest) =>
      audiencesService.createEntry(audienceId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audience-entries', audienceId] });
      queryClient.invalidateQueries({ queryKey: ['audience', audienceId] });
      queryClient.invalidateQueries({ queryKey: ['audiences'] });
    },
  });
};

export const useUpdateAudienceEntry = (
  audienceId: string | undefined
): UseMutationResult<
  AudienceEntryResponse,
  Error,
  { entryId: string; data: UpdateAudienceEntryRequest }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, data }) =>
      audiencesService.updateEntry(audienceId!, entryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audience-entries', audienceId] });
    },
  });
};

export const useDeleteAudienceEntry = (
  audienceId: string | undefined
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => audiencesService.deleteEntry(audienceId!, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audience-entries', audienceId] });
      queryClient.invalidateQueries({ queryKey: ['audience', audienceId] });
      queryClient.invalidateQueries({ queryKey: ['audiences'] });
    },
  });
};
